/**
 * Layer 4 — Narrative texture. Prose (§11). One-liners about how a scene
 * went. The only lossy layer, the only place the model's words are kept,
 * and the only store with a cap. Nothing in the sim may depend on it.
 */

import { entityTag, mintTextureId } from '../../sim/ids.js';
import { compareTime } from '../../sim/time.js';
import { TUNING } from '../../sim/tuning.js';
import type { EntityRef, NarrativeTexture, WorldState, WorldTime } from '../../sim/types/index.js';

export function addTexture(
  state: WorldState,
  prose: string,
  concerning: readonly EntityRef[],
  at: WorldTime = state.time,
): { readonly state: WorldState; readonly texture: NarrativeTexture } {
  const minted = mintTextureId(state);
  const texture: NarrativeTexture = { id: minted.id, at, concerning, prose };
  const kept = [...minted.state.memory.texture, texture];
  // Lossy on purpose: drop the oldest past the cap. Losing it costs nothing
  // structural (§11).
  const overflow = Math.max(0, kept.length - TUNING.memory.textureCap);
  return {
    texture,
    state: {
      ...minted.state,
      memory: { ...minted.state.memory, texture: kept.slice(overflow) },
    },
  };
}

/** Texture that mentions this entity, newest first. */
export function textureConcerning(state: WorldState, ref: EntityRef): NarrativeTexture[] {
  const tag = entityTag(ref);
  return state.memory.texture
    .filter((t) => t.concerning.some((c) => entityTag(c) === tag))
    .sort((x, y) => compareTime(y.at, x.at));
}
